import { useEffect, useRef, useState } from "react";

interface Book {
  title: string;
  lesson: string;
  description: string;
  image: string;
  gumroadUrl: string;
  isFeatured?: boolean;
}

const books: Book[] = [
  {
    title: "Cappy and Barry's Big Muddy Mess",
    lesson: "Saying sorry & cleaning up",
    description:
      "A story about saying sorry and cleaning up after yourself.",
    image:
      "../../../../images/6c68212f97f749b410f8e16a743a7a26.png",
    gumroadUrl: "https://cappybarry.gumroad.com/",
  },
  {
    title: "Cappy and Barry and the Missing Mango",
    lesson: "Honesty, sharing & truth",
    description:
      "A gentle lesson about honesty, sharing, and telling the truth.",
    image:
      "../../../../images/665a5790f1a4df1cc5fc35383ce99091.png",
    gumroadUrl: "https://cappybarry.gumroad.com/l/missingmango",
    isFeatured: true,
  },
  {
    title: "Cappy and Barry Splash Too Far",
    lesson: "Listening & staying safe",
    description:
      "A fun adventure about listening carefully and staying safe.",
    image:
      "../../../../images/1e59e37eeb0a27b71ef1496f87ef8738.png",
    gumroadUrl: "https://cappybarry.gumroad.com/",
  },
  {
    title: "Cappy and Barry's Wobbly Bridge Adventure",
    lesson: "Bravery, teamwork & helping",
    description:
      "A story about bravery, teamwork, and helping a friend.",
    image:
      "../../../../images/cc3a53a3000b37030d8c7f4edd3b3fdc.png",
    gumroadUrl: "https://cappybarry.gumroad.com/",
  },
  {
    title: "Cappy and Barry and the Grumpy Goose",
    lesson: "Kindness, patience & understanding",
    description:
      "A sweet lesson about kindness, patience, and understanding others.",
    image:
      "../../../../images/305084413bbea2e67a8172f1976f181c.png",
    gumroadUrl: "https://cappybarry.gumroad.com/",
  },
];

export default function BooksSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="books"
      className="relative py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50 overflow-hidden"
    >
      <div
        ref={ref}
        className={`relative z-10 w-full transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="max-w-5xl mx-auto mb-10 md:mb-14">
          <img
            src="https://storage.readdy-site.link/project_files/c76c301d-9bec-452e-bd6e-c8e234254877/e876575e-7373-4238-bff2-d482699078d4_compressed_ChatGPT-Image-Jun-26-2026-07_26_46-AM.webp"
            alt="Cappy and Barry Adventures - Fun-filled stories with gentle life lessons"
            className="w-full h-auto rounded-2xl shadow-2xl shadow-foreground-950/10 object-cover"
          />
        </div>

        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-sm font-semibold rounded-full mb-4">
            The Series
          </span>
          <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
            5 Heartwarming Stories
          </h2>
          <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto">
            Each adventure teaches a valuable lesson, wrapped in a story your
            little one will ask for again and again.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-8">
          {books.map((book, index) => (
            <div
              key={index}
              className={`group flex flex-col bg-white rounded-2xl border border-background-200 overflow-hidden hover:shadow-lg transition-all duration-500 hover:-translate-y-1 relative ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-background-100 flex items-center justify-center p-4">
                <img
                  alt={book.title}
                  className="w-full h-full object-contain"
                  src={book.image}
                />
                {book.isFeatured && (
                  <div className="absolute top-3 left-3 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    Now Available
                  </div>
                )}
              </div>
              <div className="p-4 md:p-5 flex flex-col flex-1">
                <h3 className="font-bold text-base md:text-lg text-foreground-950 leading-tight mb-2">
                  {book.title}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-heart-3-fill text-accent-500 text-sm" />
                  <span className="text-sm text-foreground-500">
                    {book.lesson}
                  </span>
                </div>
                <p className="text-sm text-foreground-500 mb-4 leading-relaxed">
                  {book.description}
                </p>
                <div className="mt-auto">
                  <a
                    href={book.gumroadUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center justify-center gap-2 w-full bg-foreground-800 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-accent-500 transition-colors"
                  >
                    View on Gumroad
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}